import { Tabs } from '../ui';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { AGENT_TASK_BUCKET_BI_SLUG } from '../../../lib/agent-task-buckets';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { useTranslation } from '../../../lib/i18n';
import type { TabKey } from './buckets';

// `all` is retained on purpose even though the strip no longer renders it — the tab is HIDDEN, not
// deleted (see `taskTabKeys` / `TasksBody`), and this entry is what it comes back with.
const TAB_META: Record<TabKey, { labelKey: string }> = {
  all: { labelKey: 'workspace.tasks.all' },
  waiting: { labelKey: 'tasks.status.actionNeeded' },
  // The in-progress TAB is labelled "Agent Tasks", not "In progress": the tab names the work, while the
  // per-task STATUS badge keeps `tasks.status.inProgress`. Two different jobs — a badge that said
  // "Agent Tasks" would state no status at all. Mirrored on iOS/Android (`AgentTasksScreen.tabTitle`,
  // `AgentTaskBucket.title`), all three reading the SAME key so the copy cannot drift.
  //
  // ⚠️ `agents.tasksAtWork` was RE-ADDED to web's 37 bundles for this. #9386 deleted it here (the agents
  // island moved to its own `agentsIsland.*` vocabulary and left the key unused) while both native
  // catalogs kept it — so this is the shared key restored, not a new one, and its values are the
  // originals recovered from git rather than fresh translations.
  inProgress: { labelKey: 'agents.tasksAtWork' },
  done: { labelKey: 'tasks.status.done' },
  failed: { labelKey: 'tasks.status.failed' },
};

// Renders exactly the tabs it is given — the caller decides which are visible (`visibleTaskTabs`, plus the
// leading `all`). Drawn as the category pages' strip (Figma 21055:246799), which carries no count pills:
// the counts are still computed, for BI and for the default-tab rule, but the tab is a plain label.
export function TaskTabs({
  tabs,
  counts,
  active,
  onSelect,
}: {
  tabs: TabKey[];
  counts: Record<TabKey, number>;
  active: TabKey;
  onSelect: (tab: TabKey) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const select = (tab: TabKey) => {
    // The tab itself is the entity acted on, and the bucket's size is the context that makes the click
    // readable ("switched to a Waiting tab holding 12"). `entity.id` is the shared slug, not the TS key —
    // see `AGENT_TASK_BUCKET_BI_SLUG`; the leading `all` catch-all has no bucket slug, so it reports as `all`.
    logPageClickBi(
      { ...TASKS_PAGE, button_name: 'switch_tab' },
      {
        componentName: `switch_tab_${tab}`,
        entity: { id: tab === 'all' ? 'all' : AGENT_TASK_BUCKET_BI_SLUG[tab], type: ENTITY_TYPE.tab },
        additionalFields: { task_count: String(counts[tab] ?? 0) },
      },
    );
    onSelect(tab);
  };
  return (
    <Tabs
      variant="category"
      label={t('home.map.bucket.tasks')}
      items={tabs.map((tab) => ({ value: tab, label: t(TAB_META[tab].labelKey) }))}
      value={active}
      onChange={select}
    />
  );
}
